import { defineStore } from 'pinia';
import { BotService } from '@/services/bot';
import { useBotsStore } from '@/stores/bots';
import { useHubUiStore } from '@/stores/hubUi';

/** State of the channels module */
export interface ChannelsState {
  /** Channel objects of the current bot (each with `channelId`, `displayName`, config, ...) */
  channels: any[];
}

/**
 * Pinia conversion of legacy vuex module `src/store/modules/channels.ts` (namespace `channels`).
 * In-memory only — no `vuexPersist` option (matches the legacy module, which had no reducer entry
 * in `src/store/index.ts`).
 *
 * Collisions & renames (see
 * `/private/tmp/claude-501/-Users-haslien-Documents-GitHub-moin-hub/23091b27-f9d9-4b85-91e6-ff6cd1c12710/scratchpad/pinia-migration/RENAMES.md`):
 * - Legacy had BOTH a state prop `channels` and an identity-passthrough getter of the same name
 *   (`channels: (state) => state.channels`). The getter is renamed `channelsGetter`; consumers
 *   that used `getters['channels/channels']` should read `useChannelsStore().channelsGetter` (or
 *   just `.channels` directly off the store, which is equivalent).
 * - The generic legacy mutation `setValue(state, { key, value })` becomes a same-named action.
 */
export const useChannelsStore = defineStore('channels', {
  state: (): ChannelsState => ({
    channels: [],
  }),
  getters: {
    /**
     * Renamed from legacy getter `channels` (collided with the `channels` state prop). Identity
     * passthrough — consumers can equivalently read `useChannelsStore().channels` directly.
     */
    channelsGetter(state: ChannelsState): any[] {
      return state.channels;
    },
  },
  actions: {
    /**
     * Converted from legacy mutation `setValue` (no matching legacy action existed). Generic
     * top-level state setter, kept for fidelity with the legacy module even though it is only
     * ever invoked internally with `key: 'channels'`.
     */
    setValue({ key, value }: { key: string; value: any }) {
      (this as any)[key] = value;
    },
    /**
     * Loads the channels of the current bot into state.
     */
    loadChannels() {
      // check if channels are already loaded
      const channels = useBotsStore().currentBotGetter.channels;
      this.setValue({ key: 'channels', value: channels });
    },
    addSnackbarMessage({ message }: { message: string }) {
      useHubUiStore().addAlert({ message, type: 'error' });
    },
    async updateChannel({ botId, channel }: { botId: string; channel: any }) {
      const { success, data: updatedBot, error } = await BotService.updateChannel({ botId, channel });

      if (!success || !updatedBot) {
        const details = error ? ` Error: ${error}` : '';
        const message = `Failed to update channel '${channel.channelId}'.${details}`;
        this.addSnackbarMessage({ message });
        return { success: false, error: message };
      }

      if (!updatedBot.channels) {
        this.addSnackbarMessage({ message: `Updated bot does not have channels` });
        return { success: false, error: 'Updated bot does not have channels' };
      }

      this.setValue({ key: 'channels', value: updatedBot.channels });
      useBotsStore().setChannel({
        botId,
        channel: updatedBot.channels.find((ch: any) => ch.channelId === channel.channelId),
      });
      return { success: true, data: updatedBot.channels };
    },
  },
});
